import { apiClient, authenticatedApiClient } from './client';
import type {
  ApiItem,
  ApiCollection,
  ApiSite,
  ApiSubmission,
  ApiEntity,
  ApiFile,
  ApiInitiative,
  ApiUser,
  ApiContent,
  ApiAccessGrant,
  ItemsResponse,
  EntitiesResponse,
  FilesResponse,
  InitiativesResponse,
  CollectionsResponse,
  ContentResponse,
  ItemsQuery,
  EntitiesQuery,
  FilesQuery,
  InitiativesQuery,
  CollectionsQuery,
  ContentQuery,
  AccessGrantsQuery,
  AccessGrantsResponse,
  CreateAccessGrantRequest,
  CreateAccessGrantResponse,
  UpdateAccessGrantRequest,
  CreateCollectionRequest,
  UpdateCollectionRequest,
  CreateSiteRequest,
  UpdateSiteRequest,
  CreateSubmissionRequest,
  UpdateSubmissionRequest,
  CreateFileRequest,
  UpdateFileRequest,
  CreateInitiativeRequest,
  UpdateInitiativeRequest,
  ProviderOpportunitiesQuery,
  ProviderOpportunitiesResponse,
  CreateContentRequest,
  UpdateContentRequest,
  SubmissionsQuery,
  SubmissionsResponse,
  Activity,
  EngagementSummary,
  ActivityMutationResponse,
  ApiQuiz,
  ApiQuizQuestion,
  ApiQuizQuestionResponse,
  ApiQuizSubmission,
  CreateQuizRequest,
  UpdateQuizRequest,
  QuizzesQuery,
  QuizzesResponse,
  QuizSubmissionsQuery,
  QuizSubmissionsResponse,
  LeaderboardResponse,
  QuizAnalyticsResponse,
  QuestionAnalyticsResponse,
  SearchQuery,
  SearchResponse,
  UsersQuery,
  UsersResponse,
  AddPointsParams,
  RemovePointsParams,
  TransferPointsParams,
  SpendPointsParams,
  PointsBalance,
  PointsHistoryQuery,
  AddPointsResponse,
  RemovePointsResponse,
  TransferPointsResponse,
  SpendPointsResponse,
  PointsHistoryResponse,
  PointsStatsResponse,
  ApiDynamicItem,
  DynamicItemsResponse,
  DynamicItemsQuery,
  CreateDynamicItemRequest,
  UpdateDynamicItemRequest,
} from './types';
import type {
  EngagementAnalytics,
  TrendAnalytics,
  ProductInsightsResponse,
  InitiativeMetricsResponse,
  SummaryResponse,
} from './analytics-types';

/**
 * Converts a ky HTTPError into a plain Error with API error details attached.
 * This prevents Next.js dev overlay from intercepting the HTTPError while
 * preserving the error message, code, and column for form-level error handling.
 */
function toApiError(error: any): Error {
  const plainError = new Error(error?.message || 'An unexpected error occurred');
  (plainError as any).code = error?.code;
  (plainError as any).column = error?.column;
  return plainError;
}

// Items API
export const itemsApi = {
  // Get paginated items with filtering
  getItems: (query?: ItemsQuery) => {
    const searchParams = { ...query } as any;
    if (Array.isArray(searchParams.collectionId)) {
      searchParams.collectionId = searchParams.collectionId.join(',');
    }
    return apiClient.get('items', { searchParams }).json<ItemsResponse>();
  },

  // Get single item by ID
  getItem: (id: string) => apiClient.get(`items/${id}`).json<ApiItem>(),

  // Create item (authenticated)
  createItem: (data: Partial<ApiItem>) =>
    authenticatedApiClient.post('items', { json: data }).json<ApiItem>(),

  // Update item
  updateItem: (id: number, data: Partial<ApiItem>) =>
    authenticatedApiClient.put(`items/${id}`, { json: data }).json<ApiItem>(),

  // Bulk update items (admin only)
  bulkUpdateItems: (itemIds: number[], updates: { active?: boolean; status?: string }) =>
    authenticatedApiClient
      .patch('items/bulk', { json: { itemIds, updates } })
      .json<{ success: boolean; updatedCount: number; message: string }>(),

  // Add a connection from an item to an entity
  addItemConnection: (
    itemId: number,
    data: { toType: string; toId: number; relationships: string[] }
  ) =>
    authenticatedApiClient
      .post(`items/${itemId}/connections`, { json: data })
      .json<{ success: boolean; connection: any }>(),

  // Remove a connection from an item
  removeItemConnection: (itemId: number, toType: string, toId: number) =>
    authenticatedApiClient
      .delete(`items/${itemId}/connections/${toType}/${toId}`, { json: {} })
      .json<{ success: boolean }>(),
};

// Collections API (uses authenticated client)
export const collectionsApi = {
  // Get user's collections
  getCollections: (query?: CollectionsQuery) => {
    // Only create searchParams if query is provided and has properties
    let searchParams: any = undefined;

    if (query && Object.keys(query).length > 0) {
      searchParams = { ...query };

      // Remove empty string values
      Object.keys(searchParams).forEach((key) => {
        if (
          searchParams[key] === '' ||
          searchParams[key] === undefined ||
          searchParams[key] === null
        ) {
          delete searchParams[key];
        }
      });

      // Handle array parameters that need joining
      if (Array.isArray(searchParams.collectionIds)) {
        searchParams.collectionIds = searchParams.collectionIds.join(',');
      }
      if (Array.isArray(searchParams.tags)) {
        searchParams.tags = searchParams.tags.join(',');
      }

      // If all params were removed, set to undefined
      if (Object.keys(searchParams).length === 0) {
        searchParams = undefined;
      }
    }

    return authenticatedApiClient.get('collections', { searchParams }).json<CollectionsResponse>();
  },

  // Get single collection with items
  getCollection: (id: number, options?: { itemLimit?: number; includeEngagement?: boolean }) =>
    authenticatedApiClient
      .get(`collections/${id}`, {
        searchParams: {
          ...(options?.itemLimit ? { limit: options.itemLimit } : {}),
          ...(options?.includeEngagement ? { includeEngagement: true } : {}),
        },
      })
      .json<ApiCollection>(),

  // Create collection
  createCollection: (data: CreateCollectionRequest) =>
    authenticatedApiClient.post('collections', { json: data }).json<ApiCollection>(),

  // Update collection
  updateCollection: (id: number, data: UpdateCollectionRequest) =>
    authenticatedApiClient.put(`collections/${id}`, { json: data }).json<ApiCollection>(),

  // Admin: Update collection tags
  updateCollectionTags: (id: number, tags: string[]) =>
    authenticatedApiClient
      .patch(`collections/${id}/tags`, { json: { tags } })
      .json<ApiCollection>(),

  // Delete collection
  deleteCollection: (id: number) =>
    authenticatedApiClient.delete(`collections/${id}`, { json: {} }),

  // Add item to collection
  addItemToCollection: (collectionId: number, itemId: string) =>
    authenticatedApiClient
      .post(`collections/${collectionId}/items`, { json: { itemId } })
      .json<{ message: string; userCollections: Array<{ id: number; name: string }> }>(),

  // Remove item from collection
  removeItemFromCollection: (collectionId: number, itemId: string) =>
    authenticatedApiClient
      .delete(`collections/${collectionId}/items/${itemId}`, { json: {} })
      .json<{ message: string; userCollections: Array<{ id: number; name: string }> }>(),
};

// Sites API
export const sitesApi = {
  // Get all active sites
  getSites: () => apiClient.get('sites').json<ApiSite[]>(),

  // Get single site
  getSite: (id: number) => apiClient.get(`sites/${id}`).json<ApiSite>(),

  // Create site
  createSite: (data: CreateSiteRequest) => apiClient.post('sites', { json: data }).json<ApiSite>(),

  // Update site
  updateSite: (id: number, data: UpdateSiteRequest) =>
    apiClient.put(`sites/${id}`, { json: data }).json<ApiSite>(),

  // Delete site
  deleteSite: (id: number) => apiClient.delete(`sites/${id}`, { json: {} }),
};

// Metadata API
export const metadataApi = {
  getMetadata: <T = any>(key: string) => apiClient.get(`metadata/${key}`).json<{ data: T }>(),
  setMetadata: <T = any>(key: string, data: T) =>
    authenticatedApiClient.post(`metadata/${key}`, { json: { data } }),
};

// Users API
export const usersApi = {
  // Get users with filtering (admin only)
  getUsers: (query?: UsersQuery) => {
    // Drop unset filters so they don't end up in the URL as "email=undefined".
    const searchParams = query
      ? Object.fromEntries(
          Object.entries(query).filter(([, value]) => value !== undefined && value !== '')
        )
      : undefined;
    return authenticatedApiClient.get('users', { searchParams }).json<UsersResponse>();
  },

  // Create new user (admin only)
  createUser: (data: {
    name: string;
    email: string;
    image?: string | null;
    approved?: boolean;
    data?: Record<string, unknown>;
  }) => authenticatedApiClient.post('users', { json: data }).json<ApiUser>(),

  // Update current user profile
  updateProfile: (data: {
    name?: string;
    tags?: string[];
    image?: string | null;
    gender?: string | null;
    birthYear?: number | null;
    locations?: string[];
    data?: Record<string, unknown>;
  }) => authenticatedApiClient.put('users/me', { json: data }).json<any>(),

  // Update any user (admin only)
  updateUser: (
    userId: number,
    data: {
      name?: string;
      email?: string;
      image?: string | null;
      tags?: string[];
      approved?: boolean;
      data?: Record<string, unknown>;
    }
  ) => authenticatedApiClient.patch(`users/${userId}`, { json: data }).json<ApiUser>(),

  // Delete user (admin only)
  deleteUser: (userId: number) =>
    authenticatedApiClient.delete(`users/${userId}`, { json: {} }).json<void>(),
};

// Submissions API
export const submissionsApi = {
  // Get user's submissions
  getSubmissions: (query?: SubmissionsQuery) => {
    let searchParams: any = undefined;

    if (query && Object.keys(query).length > 0) {
      searchParams = { ...query };

      // Handle array parameters that need joining
      if (Array.isArray(searchParams.tags)) {
        searchParams.tags = searchParams.tags.join(',');
      }
    }

    return authenticatedApiClient.get('submissions', { searchParams }).json<SubmissionsResponse>();
  },

  // Get single submission
  getSubmission: (id: number) =>
    authenticatedApiClient.get(`submissions/${id}`).json<ApiSubmission>(),

  // Create submission
  createSubmission: (data: CreateSubmissionRequest) =>
    authenticatedApiClient.post('submissions', { json: data }).json<ApiSubmission>(),

  // Update submission
  updateSubmission: (id: number, data: UpdateSubmissionRequest) =>
    authenticatedApiClient.put(`submissions/${id}`, { json: data }).json<ApiSubmission>(),

  // Delete submission
  deleteSubmission: (id: number) =>
    authenticatedApiClient.delete(`submissions/${id}`, { json: {} }),
};

// Entities API
export const entitiesApi = {
  // Get entities with filtering
  getEntities: (query?: EntitiesQuery) => {
    const searchParams = { ...query } as any;
    if (Array.isArray(searchParams.type)) {
      searchParams.type = searchParams.type.join(',');
    }
    if (Array.isArray(searchParams.roles)) {
      searchParams.roles = searchParams.roles.join(',');
    }
    if (Array.isArray(searchParams.capabilities)) {
      searchParams.capabilities = searchParams.capabilities.join(',');
    }
    if (Array.isArray(searchParams.specialisations)) {
      searchParams.specialisations = searchParams.specialisations.join(',');
    }
    if (Array.isArray(searchParams.channelExpertise)) {
      searchParams.channelExpertise = searchParams.channelExpertise.join(',');
    }
    if (Array.isArray(searchParams.geoPresence)) {
      searchParams.geoPresence = searchParams.geoPresence.join(',');
    }
    if (Array.isArray(searchParams.geoExpertise)) {
      searchParams.geoExpertise = searchParams.geoExpertise.join(',');
    }
    if (Array.isArray(searchParams.themes)) {
      searchParams.themes = searchParams.themes.join(',');
    }
    if (Array.isArray(searchParams.systemTags)) {
      searchParams.systemTags = searchParams.systemTags.join(',');
    }
    if (Array.isArray(searchParams.requiredSystemTags)) {
      searchParams.requiredSystemTags = searchParams.requiredSystemTags.join(',');
    }
    if (Array.isArray(searchParams.optionalSystemTags)) {
      searchParams.optionalSystemTags = searchParams.optionalSystemTags.join(',');
    }
    if (searchParams.tagExpression && typeof searchParams.tagExpression === 'object') {
      searchParams.tagExpression = JSON.stringify(searchParams.tagExpression);
    }
    if (Array.isArray(searchParams.initiativeStatus)) {
      searchParams.initiativeStatus = searchParams.initiativeStatus.join(',');
    }
    return apiClient.get('entities', { searchParams }).json<EntitiesResponse>();
  },

  // Get single entity by ID
  getEntity: (
    id: string,
    query?: { includeInitiatives?: boolean; initiativeStatus?: string[] }
  ) => {
    const searchParams: any = {};
    if (query?.includeInitiatives) {
      searchParams.includeInitiatives = 'true';
    }
    if (query?.initiativeStatus) {
      searchParams.initiativeStatus = query.initiativeStatus.join(',');
    }
    return apiClient
      .get(`entities/${id}`, {
        searchParams: Object.keys(searchParams).length > 0 ? searchParams : undefined,
      })
      .json<ApiEntity>();
  },

  // Get entity initiatives with visibility filtering
  getEntityInitiatives: (id: string, query?: { status?: string[]; limit?: number }) => {
    const searchParams: any = {};
    if (query?.status) {
      searchParams.status = query.status.join(',');
    }
    if (query?.limit) {
      searchParams.limit = query.limit.toString();
    }
    return apiClient
      .get(`entities/${id}/initiatives`, { searchParams })
      .json<{ initiatives: Array<{ id: number; name: string; type: string; status: string }> }>();
  },

  // Create entity (admin only)
  createEntity: async (data: Partial<ApiEntity>) => {
    try {
      return await apiClient.post('entities', { json: data }).json<ApiEntity>();
    } catch (error: any) {
      throw toApiError(error);
    }
  },

  // Update entity (admin only)
  updateEntity: async (id: string, data: Partial<ApiEntity>) => {
    try {
      return await authenticatedApiClient.put(`entities/${id}`, { json: data }).json<ApiEntity>();
    } catch (error: any) {
      throw toApiError(error);
    }
  },
};

// Calendar API
export const calendarApi = {
  // Get calendars with filtering
  getCalendars: (query?: {
    limit?: number;
    type?: string | string[];
    status?: string;
    visibility?: string;
    search?: string;
    deleted?: boolean;
    ownerId?: string;
    ownerType?: string;
    creatorId?: string;
    creatorType?: string;
    tags?: string[];
    nextCursor?: string;
    prevCursor?: string;
    includeInitiatives?: boolean;
    initiativeStatus?: string[];
  }) => {
    const searchParams = { ...query } as any;
    if (Array.isArray(searchParams.type)) {
      searchParams.type = searchParams.type.join(',');
    }
    if (Array.isArray(searchParams.tags)) {
      searchParams.tags = searchParams.tags.join(',');
    }
    if (Array.isArray(searchParams.initiativeStatus)) {
      searchParams.initiativeStatus = searchParams.initiativeStatus.join(',');
    }
    if (searchParams.includeInitiatives !== undefined) {
      searchParams.includeInitiatives = searchParams.includeInitiatives.toString();
    }
    if (searchParams.deleted !== undefined) {
      searchParams.deleted = searchParams.deleted.toString();
    }
    return apiClient.get('calendar', { searchParams }).json<any>();
  },

  // Get single calendar by ID
  getCalendar: (
    id: number,
    query?: { includeInitiatives?: boolean; initiativeStatus?: string[] }
  ) => {
    const searchParams: any = {};
    if (query?.includeInitiatives) {
      searchParams.includeInitiatives = 'true';
    }
    if (query?.initiativeStatus) {
      searchParams.initiativeStatus = query.initiativeStatus.join(',');
    }
    return apiClient
      .get(`calendar/${id}`, {
        searchParams: Object.keys(searchParams).length > 0 ? searchParams : undefined,
      })
      .json<any>();
  },

  // Get calendar initiatives
  getCalendarInitiatives: (id: number, query?: { status?: string[]; limit?: number }) => {
    const searchParams: any = {};
    if (query?.status) {
      searchParams.status = query.status.join(',');
    }
    if (query?.limit) {
      searchParams.limit = query.limit.toString();
    }
    return apiClient
      .get(`calendar/${id}/initiatives`, { searchParams })
      .json<{ initiatives: Array<{ id: number; name: string; type: string; status: string }> }>();
  },

  // Create calendar (admin only)
  createCalendar: (data: any) =>
    authenticatedApiClient.post('calendar', { json: data }).json<any>(),

  // Update calendar (admin only)
  updateCalendar: (id: number, data: any) =>
    authenticatedApiClient.put(`calendar/${id}`, { json: data }).json<any>(),

  // Delete calendar (admin only, soft delete)
  deleteCalendar: (id: number) =>
    authenticatedApiClient.delete(`calendar/${id}`, { json: {} }).json<{ message: string }>(),
};

// Activities API
export const activitiesApi = {
  // Comments
  createComment: (data: {
    objectId: string;
    objectType: string;
    content: string;
    includeEngagement?: boolean;
  }) =>
    authenticatedApiClient
      .post('activities/comments', { json: data })
      .json<Activity | ActivityMutationResponse>(),

  deleteComment: (activityId: string) =>
    authenticatedApiClient.delete(`activities/${activityId}`, { json: {} }),

  // Reactions
  addReaction: (data: {
    objectId: string;
    objectType: string;
    reactionType: string;
    includeEngagement?: boolean;
  }) =>
    authenticatedApiClient
      .post('activities/reactions', { json: data })
      .json<Activity | ActivityMutationResponse>(),

  removeReaction: async (
    objectId: string,
    objectType: string,
    reactionType?: string,
    includeEngagement?: boolean
  ) => {
    const response = await authenticatedApiClient.delete('activities/reactions', {
      json: {
        objectId,
        objectType,
        ...(reactionType && { reactionType }),
        ...(includeEngagement && { includeEngagement }),
      },
    });

    // If status is 204 (No Content), return undefined
    if (response.status === 204) {
      return undefined;
    }

    // Otherwise parse and return the JSON response
    return response.json<{ engagement: EngagementSummary }>();
  },

  changeReaction: (data: {
    objectId: string;
    objectType: string;
    reactionType: string;
    includeEngagement?: boolean;
  }) =>
    authenticatedApiClient
      .put('activities/reactions', { json: data })
      .json<Activity | ActivityMutationResponse>(),

  // Votes
  addVote: (data: {
    objectId: string;
    objectType: string;
    vote: 'up' | 'down' | 'interested';
    includeEngagement?: boolean;
    entities?: Array<{ id: number; name: string }>;
    note?: string;
    brand?: string;
    timeframe?: string;
  }) =>
    authenticatedApiClient
      .post('activities/votes', { json: data })
      .json<Activity | ActivityMutationResponse>(),

  removeVote: async (objectId: string, objectType: string, includeEngagement?: boolean) => {
    const response = await authenticatedApiClient.delete('activities/votes', {
      json: {
        objectId,
        objectType,
        ...(includeEngagement && { includeEngagement }),
      },
    });

    // If status is 204 (No Content), return undefined
    if (response.status === 204) {
      return undefined;
    }

    // Otherwise parse and return the JSON response
    return response.json<{ engagement: EngagementSummary }>();
  },

  changeVote: (data: {
    objectId: string;
    objectType: string;
    vote: 'up' | 'down' | 'interested';
    includeEngagement?: boolean;
  }) =>
    authenticatedApiClient
      .put('activities/votes', { json: data })
      .json<Activity | ActivityMutationResponse>(),

  // Attendance
  addAttendance: (data: { objectId: string; objectType: string; includeEngagement?: boolean }) =>
    authenticatedApiClient
      .post('activities/attendance', { json: data })
      .json<Activity | ActivityMutationResponse>(),

  removeAttendance: async (objectId: string, objectType: string, includeEngagement?: boolean) => {
    const response = await authenticatedApiClient.delete('activities/attendance', {
      json: {
        objectId,
        objectType,
        ...(includeEngagement && { includeEngagement }),
      },
    });

    if (response.status === 204) {
      return undefined;
    }

    return response.json<{ engagement: EngagementSummary }>();
  },

  updateAttendanceStatus: (
    activityId: string,
    status: 'approved' | 'declined' | 'registered' | 'pending_approval',
    options?: { sendEmail?: boolean; emailSubject?: string; emailBody?: string }
  ) =>
    authenticatedApiClient
      .patch(`activities/attendance/${activityId}/status`, {
        json: { status, ...options },
      })
      .json<Activity>(),

  bulkAddAttendance: (data: { objectId: string; objectType: string; userIds: number[] }) =>
    authenticatedApiClient
      .post('activities/attendance/bulk', { json: data })
      .json<{ success: boolean; addedCount: number; activities: Activity[]; errors?: number[] }>(),

  // Feeds
  getObjectFeed: (
    objectId: string,
    objectType: string,
    params?: { limit?: number; offset?: number }
  ) =>
    authenticatedApiClient
      .get(`activities/object/${objectType}/${objectId}`, { searchParams: params })
      .json<Activity[]>(),

  getUserFeed: (userId: string, params?: { limit?: number; offset?: number }) =>
    authenticatedApiClient
      .get(`activities/user/${userId}`, { searchParams: params })
      .json<Activity[]>(),

  getEngagementSummary: (objectId: string, objectType: string) =>
    apiClient.get(`activities/engagement/${objectType}/${objectId}`).json<EngagementSummary>(),

  getEntityInterest: (
    entityId: number,
    params?: {
      limit?: number;
      offset?: number;
      initiativeName?: string;
      initiativeStatus?: string;
      initiativeIds?: string;
      entityIds?: string;
      userIds?: string;
    }
  ) => {
    const searchParams: any = {};
    if (params?.limit) searchParams.limit = params.limit.toString();
    if (params?.offset !== undefined) searchParams.offset = params.offset.toString();
    if (params?.initiativeName) searchParams.initiativeName = params.initiativeName;
    if (params?.initiativeStatus) searchParams.initiativeStatus = params.initiativeStatus;
    if (params?.initiativeIds) searchParams.initiativeIds = params.initiativeIds;
    if (params?.entityIds) searchParams.entityIds = params.entityIds;
    if (params?.userIds) searchParams.userIds = params.userIds;
    return authenticatedApiClient
      .get(`activities/entity-interest/${entityId}`, { searchParams })
      .json<{
        items: Array<
          Activity & {
            initiative?: { id: number; name: string; status: string; type: string };
          }
        >;
        pagination: { total: number; limit: number; offset: number; hasMore: boolean };
      }>();
  },

  bulkDeleteActivities: (activityIds: string[]) =>
    authenticatedApiClient
      .delete('activities/bulk', { json: { activityIds } })
      .json<{ success: boolean; deletedCount: number }>(),

  getMyInterest: (params?: {
    limit?: number;
    offset?: number;
    initiativeStatus?: string;
    entityIds?: string;
    representingEntityIds?: string;
  }) => {
    const searchParams: any = {};
    if (params?.limit) searchParams.limit = params.limit.toString();
    if (params?.offset !== undefined) searchParams.offset = params.offset.toString();
    if (params?.initiativeStatus) searchParams.initiativeStatus = params.initiativeStatus;
    if (params?.entityIds) searchParams.entityIds = params.entityIds;
    if (params?.representingEntityIds)
      searchParams.representingEntityIds = params.representingEntityIds;
    return authenticatedApiClient.get('activities/my-interest', { searchParams }).json<{
      items: Array<
        Activity & {
          initiative?: { id: number; name: string; status: string; type: string };
          ownerEntity?: { id: number; name: string };
        }
      >;
      pagination: { total: number; limit: number; offset: number; hasMore: boolean };
    }>();
  },

  getEntityRepresentingInterest: (
    entityId: number,
    params?: {
      limit?: number;
      offset?: number;
      initiativeStatus?: string;
      userIds?: string;
      initiativeIds?: string;
    }
  ) => {
    const searchParams: any = {};
    if (params?.limit) searchParams.limit = params.limit.toString();
    if (params?.offset !== undefined) searchParams.offset = params.offset.toString();
    if (params?.initiativeStatus) searchParams.initiativeStatus = params.initiativeStatus;
    if (params?.userIds) searchParams.userIds = params.userIds;
    if (params?.initiativeIds) searchParams.initiativeIds = params.initiativeIds;
    return authenticatedApiClient
      .get(`activities/entity-representing-interest/${entityId}`, { searchParams })
      .json<{
        items: Array<
          Activity & {
            initiative?: { id: number; name: string; status: string; type: string };
            ownerEntity?: { id: number; name: string };
          }
        >;
        pagination: { total: number; limit: number; offset: number; hasMore: boolean };
      }>();
  },

  getEntityRepresentingInterestFilters: (entityId: number) =>
    authenticatedApiClient.get(`activities/entity-representing-interest/${entityId}/filters`).json<{
      teamMembers: Array<{ id: number; name: string | null; email: string; image: string | null }>;
      initiatives: Array<{ id: number; name: string }>;
    }>(),

  getEntityFeed: (
    entityId: number,
    params?: {
      limit?: number;
      offset?: number;
      types?: string[];
      startDate?: string;
      endDate?: string;
      userId?: string;
      initiativeId?: string;
    }
  ) => {
    const searchParams: any = {};
    if (params?.limit) searchParams.limit = params.limit.toString();
    if (params?.offset !== undefined) searchParams.offset = params.offset.toString();
    if (params?.types && params.types.length > 0) searchParams.types = params.types.join(',');
    if (params?.startDate) searchParams.startDate = params.startDate;
    if (params?.endDate) searchParams.endDate = params.endDate;
    if (params?.userId) searchParams.userId = params.userId;
    if (params?.initiativeId) searchParams.initiativeId = params.initiativeId;
    return authenticatedApiClient.get(`activities/entity-feed/${entityId}`, { searchParams }).json<{
      items: Array<
        Activity & {
          user?: { id: number; name: string | null; email: string; image: string | null };
          objectName?: string;
        }
      >;
      pagination: { total: number; limit: number; offset: number; hasMore: boolean };
    }>();
  },
};

// Health check
export const healthApi = {
  checkHealth: () => apiClient.get('health').json<{ status: string; timestamp: string }>(),
};

// Files API
export const filesApi = {
  // Get files with filtering
  getFiles: (query?: FilesQuery) => {
    const searchParams = { ...query } as any;
    if (Array.isArray(searchParams.type)) {
      searchParams.type = searchParams.type.join(',');
    }
    if (Array.isArray(searchParams.tags)) {
      searchParams.tags = searchParams.tags.join(',');
    }
    return apiClient.get('files', { searchParams }).json<FilesResponse>();
  },

  // Get single file by ID
  getFile: (id: number) => apiClient.get(`files/${id}`).json<ApiFile>(),

  // Create file (authenticated)
  createFile: (data: CreateFileRequest) =>
    authenticatedApiClient.post('files', { json: data }).json<ApiFile>(),

  // Update file (authenticated)
  updateFile: (id: number, data: UpdateFileRequest) =>
    authenticatedApiClient.put(`files/${id}`, { json: data }).json<ApiFile>(),

  // Delete file (authenticated, soft delete)
  deleteFile: (id: number) =>
    authenticatedApiClient.delete(`files/${id}`, { json: {} }).json<{ message: string }>(),

  // Permanently delete file (admin only)
  permanentlyDeleteFile: (id: number) =>
    authenticatedApiClient
      .delete(`files/${id}/permanent`, { json: {} })
      .json<{ message: string }>(),
};

// Initiatives API
export const initiativesApi = {
  // Get initiatives with filtering (public)
  getInitiatives: (query?: InitiativesQuery, options?: { accessToken?: string }) => {
    const searchParams = { ...query } as any;
    if (Array.isArray(searchParams.type)) {
      searchParams.type = searchParams.type.join(',');
    }
    if (Array.isArray(searchParams.status)) {
      searchParams.status = searchParams.status.join(',');
    }
    if (Array.isArray(searchParams.themes)) {
      searchParams.themes = searchParams.themes.join(',');
    }
    if (Array.isArray(searchParams.activities)) {
      searchParams.activities = searchParams.activities.join(',');
    }
    if (Array.isArray(searchParams.productCategories)) {
      searchParams.productCategories = searchParams.productCategories.join(',');
    }
    if (Array.isArray(searchParams.excludeActivationRoles)) {
      searchParams.excludeActivationRoles = searchParams.excludeActivationRoles.join(',');
    }
    if (Array.isArray(searchParams.systemTags)) {
      searchParams.systemTags = searchParams.systemTags.join(',');
    }
    if (Array.isArray(searchParams.requiredSystemTags)) {
      searchParams.requiredSystemTags = searchParams.requiredSystemTags.join(',');
    }
    if (Array.isArray(searchParams.optionalSystemTags)) {
      searchParams.optionalSystemTags = searchParams.optionalSystemTags.join(',');
    }
    if (searchParams.tagExpression && typeof searchParams.tagExpression === 'object') {
      searchParams.tagExpression = JSON.stringify(searchParams.tagExpression);
    }
    const headers = options?.accessToken ? { 'x-access-token': options.accessToken } : undefined;
    return apiClient
      .get('initiatives', { searchParams, ...(headers ? { headers } : {}) })
      .json<InitiativesResponse>();
  },

  // Get single initiative by ID (public)
  getInitiative: (id: number, options?: { accessToken?: string }) => {
    const headers = options?.accessToken ? { 'x-access-token': options.accessToken } : undefined;
    return apiClient
      .get(`initiatives/${id}`, headers ? { headers } : undefined)
      .json<ApiInitiative>();
  },

  // Create initiative (admin only)
  createInitiative: (data: CreateInitiativeRequest) =>
    authenticatedApiClient.post('initiatives', { json: data }).json<ApiInitiative>(),

  // Update initiative (admin only)
  updateInitiative: (id: number, data: UpdateInitiativeRequest) =>
    authenticatedApiClient.put(`initiatives/${id}`, { json: data }).json<ApiInitiative>(),

  // Delete initiative (admin only, soft delete)
  deleteInitiative: (id: number) =>
    authenticatedApiClient.delete(`initiatives/${id}`, { json: {} }).json<{ message: string }>(),

  // Clone an initiative (e.g. from template)
  cloneInitiative: (id: number, data: any) =>
    authenticatedApiClient.post(`initiatives/${id}/clone`, { json: data }).json<ApiInitiative>(),

  // Participate in an initiative (public/consumer)
  participate: (id: number) =>
    authenticatedApiClient
      .post(`initiatives/${id}/participate`, { json: {} })
      .json<{ success: boolean; connection: any }>(),

  // Get provider opportunities (components with needsProvider=true)
  getProviderOpportunities: (query?: ProviderOpportunitiesQuery) => {
    const searchParams = { ...query } as any;
    if (Array.isArray(searchParams.initiativeType)) {
      searchParams.initiativeType = searchParams.initiativeType.join(',');
    }
    return apiClient
      .get('initiatives/provider-opportunities', { searchParams })
      .json<ProviderOpportunitiesResponse>();
  },

  // Bulk update initiatives (admin only)
  bulkUpdateInitiatives: (initiativeIds: number[], updates: { status?: string }) =>
    authenticatedApiClient
      .patch('initiatives/bulk', { json: { initiativeIds, updates } })
      .json<{ success: boolean; updatedCount: number; message: string }>(),

  // Send bulk email to assignment connections
  sendBulkAssignmentEmail: (
    initiativeId: number,
    data: { connectionIds: number[]; subject: string; message: string }
  ) =>
    authenticatedApiClient
      .post(`initiatives/${initiativeId}/assignments/bulk-email`, { json: data })
      .json<{ success: boolean; sentCount: number; errors?: string[] }>(),

  // Bulk assign a user or entity to multiple initiatives at once
  bulkAssign: (data: {
    initiativeIds: number[];
    fromType: 'entity' | 'user';
    fromId: number;
    roles: string[];
    notes?: string;
    representing?: Array<{ id: number; name: string }>;
  }) =>
    authenticatedApiClient.post('initiatives/bulk-assign', { json: data }).json<{
      success: boolean;
      assignedCount: number;
      totalRequested: number;
      errors?: Array<{ initiativeId: number; success: boolean; error?: string }>;
    }>(),
};

// Import API
export const importApi = {
  // Import product from URL or markdown
  importProduct: async (data: {
    source: 'url' | 'markdown';
    url?: string;
    content?: string;
    entityId: number;
  }) => {
    try {
      return await authenticatedApiClient
        .post('import/product', {
          json: data,
          timeout: 120000, // 2 minute timeout for LLM processing
        })
        .json<{
          success: boolean;
          product: {
            id: number;
            title: string;
            handle: string;
            entityId: number;
            variantCount: number;
          };
          structuredData: any;
        }>();
    } catch (error: any) {
      if (error?.response?.status === 401) {
        throw new Error('You must be logged in to import products');
      }
      if (error?.response?.status === 403) {
        throw new Error('You do not have permission to import products');
      }
      if (error?.response?.status === 404) {
        throw new Error('Brand not found');
      }
      if (error?.response?.status === 400) {
        const errorData = await error.response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || 'Invalid import data');
      }
      const errorData = await error.response?.json().catch(() => ({}));
      throw new Error(errorData.error?.message || error?.message || 'Failed to import product');
    }
  },
};

// Quizzes API
export const quizzesApi = {
  // Get quizzes with filtering
  getQuizzes: async (query?: QuizzesQuery) => {
    try {
      const searchParams = query ? { ...query } : undefined;
      return await apiClient.get('quizzes', { searchParams }).json<QuizzesResponse>();
    } catch (error: any) {
      throw new Error(
        error?.response?.data?.message || error?.message || 'Failed to fetch quizzes'
      );
    }
  },

  // Get single quiz by ID
  getQuiz: async (id: number) => {
    try {
      return await apiClient.get(`quizzes/${id}`).json<ApiQuiz>();
    } catch (error: any) {
      if (error?.response?.status === 404) {
        throw new Error('Quiz not found');
      }
      if (error?.response?.status === 403) {
        throw new Error('You do not have permission to view this quiz');
      }
      throw new Error(error?.response?.data?.message || error?.message || 'Failed to fetch quiz');
    }
  },

  // Get quiz with all related data (collections, items) for playing
  getQuizWithData: async (id: number) => {
    try {
      return await apiClient.get(`quizzes/${id}/data`).json<{
        quiz: ApiQuiz;
        collections: Array<{ id: number; name: string; items: any[] }>;
        items: any[];
      }>();
    } catch (error: any) {
      if (error?.response?.status === 404) {
        throw new Error('Quiz not found');
      }
      if (error?.response?.status === 403) {
        throw new Error('You do not have permission to view this quiz');
      }
      throw new Error(
        error?.response?.data?.message || error?.message || 'Failed to fetch quiz data'
      );
    }
  },

  // Create quiz (authenticated)
  createQuiz: async (data: CreateQuizRequest) => {
    try {
      return await authenticatedApiClient.post('quizzes', { json: data }).json<ApiQuiz>();
    } catch (error: any) {
      if (error?.response?.status === 401) {
        throw new Error('You must be logged in to create a quiz');
      }
      if (error?.response?.status === 400) {
        throw new Error(
          error?.response?.data?.message || 'Invalid quiz data. Please check your input.'
        );
      }
      throw new Error(error?.response?.data?.message || error?.message || 'Failed to create quiz');
    }
  },

  // Update quiz (authenticated)
  updateQuiz: async (id: number, data: UpdateQuizRequest) => {
    try {
      return await authenticatedApiClient.patch(`quizzes/${id}`, { json: data }).json<ApiQuiz>();
    } catch (error: any) {
      if (error?.response?.status === 401) {
        throw new Error('You must be logged in to update a quiz');
      }
      if (error?.response?.status === 403) {
        throw new Error('You do not have permission to update this quiz');
      }
      if (error?.response?.status === 404) {
        throw new Error('Quiz not found');
      }
      if (error?.response?.status === 400) {
        throw new Error(
          error?.response?.data?.message ||
            'Cannot update quiz. It may be active with existing submissions.'
        );
      }
      throw new Error(error?.response?.data?.message || error?.message || 'Failed to update quiz');
    }
  },

  // Delete quiz (authenticated)
  deleteQuiz: async (id: number) => {
    try {
      return await authenticatedApiClient.delete(`quizzes/${id}`, { json: {} });
    } catch (error: any) {
      if (error?.response?.status === 401) {
        throw new Error('You must be logged in to delete a quiz');
      }
      if (error?.response?.status === 403) {
        throw new Error('You do not have permission to delete this quiz');
      }
      if (error?.response?.status === 404) {
        throw new Error('Quiz not found');
      }
      throw new Error(error?.response?.data?.message || error?.message || 'Failed to delete quiz');
    }
  },

  // Activate quiz
  activateQuiz: async (id: number) => {
    try {
      return await authenticatedApiClient
        .post(`quizzes/${id}/activate`, { json: {} })
        .json<ApiQuiz>();
    } catch (error: any) {
      if (error?.response?.status === 401) {
        throw new Error('You must be logged in to activate a quiz');
      }
      if (error?.response?.status === 403) {
        throw new Error('You do not have permission to activate this quiz');
      }
      if (error?.response?.status === 404) {
        throw new Error('Quiz not found');
      }
      if (error?.response?.status === 400) {
        throw new Error(
          error?.response?.data?.message || 'Cannot activate quiz. It may already be active.'
        );
      }
      throw new Error(
        error?.response?.data?.message || error?.message || 'Failed to activate quiz'
      );
    }
  },

  // Close quiz
  closeQuiz: async (id: number) => {
    try {
      return await authenticatedApiClient.post(`quizzes/${id}/close`, { json: {} }).json<ApiQuiz>();
    } catch (error: any) {
      if (error?.response?.status === 401) {
        throw new Error('You must be logged in to close a quiz');
      }
      if (error?.response?.status === 403) {
        throw new Error('You do not have permission to close this quiz');
      }
      if (error?.response?.status === 404) {
        throw new Error('Quiz not found');
      }
      if (error?.response?.status === 400) {
        throw new Error(
          error?.response?.data?.message || 'Cannot close quiz. It may not be active.'
        );
      }
      throw new Error(error?.response?.data?.message || error?.message || 'Failed to close quiz');
    }
  },

  // Archive quiz
  archiveQuiz: async (id: number) => {
    try {
      return await authenticatedApiClient
        .post(`quizzes/${id}/archive`, { json: {} })
        .json<ApiQuiz>();
    } catch (error: any) {
      if (error?.response?.status === 401) {
        throw new Error('You must be logged in to archive a quiz');
      }
      if (error?.response?.status === 403) {
        throw new Error('You do not have permission to archive this quiz');
      }
      if (error?.response?.status === 404) {
        throw new Error('Quiz not found');
      }
      throw new Error(error?.response?.data?.message || error?.message || 'Failed to archive quiz');
    }
  },

  // Question management
  addQuestion: async (id: number, question: ApiQuizQuestion) => {
    try {
      return await authenticatedApiClient
        .post(`quizzes/${id}/questions`, { json: { question } })
        .json<ApiQuiz>();
    } catch (error: any) {
      if (error?.response?.status === 401) {
        throw new Error('You must be logged in to add a question');
      }
      if (error?.response?.status === 403) {
        throw new Error('You do not have permission to modify this quiz');
      }
      if (error?.response?.status === 404) {
        throw new Error('Quiz not found');
      }
      if (error?.response?.status === 400) {
        throw new Error(
          error?.response?.data?.message ||
            'Cannot add question. Quiz may be active with submissions.'
        );
      }
      throw new Error(error?.response?.data?.message || error?.message || 'Failed to add question');
    }
  },

  updateQuestion: async (id: number, questionId: string, updates: Partial<ApiQuizQuestion>) => {
    try {
      return await authenticatedApiClient
        .patch(`quizzes/${id}/questions/${questionId}`, { json: { updates } })
        .json<ApiQuiz>();
    } catch (error: any) {
      if (error?.response?.status === 401) {
        throw new Error('You must be logged in to update a question');
      }
      if (error?.response?.status === 403) {
        throw new Error('You do not have permission to modify this quiz');
      }
      if (error?.response?.status === 404) {
        throw new Error('Quiz or question not found');
      }
      if (error?.response?.status === 400) {
        throw new Error(
          error?.response?.data?.message ||
            'Cannot update question. Quiz may be active with submissions.'
        );
      }
      throw new Error(
        error?.response?.data?.message || error?.message || 'Failed to update question'
      );
    }
  },

  deleteQuestion: async (id: number, questionId: string) => {
    try {
      return await authenticatedApiClient.delete(`quizzes/${id}/questions/${questionId}`, {
        json: {},
      });
    } catch (error: any) {
      if (error?.response?.status === 401) {
        throw new Error('You must be logged in to delete a question');
      }
      if (error?.response?.status === 403) {
        throw new Error('You do not have permission to modify this quiz');
      }
      if (error?.response?.status === 404) {
        throw new Error('Quiz or question not found');
      }
      if (error?.response?.status === 400) {
        throw new Error(
          error?.response?.data?.message ||
            'Cannot delete question. Quiz may be active with submissions.'
        );
      }
      throw new Error(
        error?.response?.data?.message || error?.message || 'Failed to delete question'
      );
    }
  },

  reorderQuestions: async (id: number, questionIds: string[]) => {
    try {
      return await authenticatedApiClient
        .post(`quizzes/${id}/questions/reorder`, { json: { questionIds } })
        .json<ApiQuiz>();
    } catch (error: any) {
      if (error?.response?.status === 401) {
        throw new Error('You must be logged in to reorder questions');
      }
      if (error?.response?.status === 403) {
        throw new Error('You do not have permission to modify this quiz');
      }
      if (error?.response?.status === 404) {
        throw new Error('Quiz not found');
      }
      if (error?.response?.status === 400) {
        throw new Error(
          error?.response?.data?.message || 'Invalid question order or missing questions'
        );
      }
      throw new Error(
        error?.response?.data?.message || error?.message || 'Failed to reorder questions'
      );
    }
  },

  // Submissions
  submitQuiz: async (id: number, responses: ApiQuizQuestionResponse[]) => {
    try {
      return await authenticatedApiClient
        .post(`quizzes/${id}/submissions`, { json: { responses } })
        .json<ApiQuizSubmission>();
    } catch (error: any) {
      if (error?.response?.status === 401) {
        throw new Error('You must be logged in to submit a quiz');
      }
      if (error?.response?.status === 403) {
        throw new Error('You do not have permission to submit this quiz');
      }
      if (error?.response?.status === 404) {
        throw new Error('Quiz not found');
      }
      if (error?.response?.status === 400) {
        throw new Error(
          error?.response?.data?.message ||
            'Cannot submit quiz. Check that all required questions are answered and submission limits are not exceeded.'
        );
      }
      throw new Error(error?.response?.data?.message || error?.message || 'Failed to submit quiz');
    }
  },

  getQuizSubmissions: async (id: number, query?: QuizSubmissionsQuery) => {
    try {
      const searchParams = query ? { ...query } : undefined;
      return await authenticatedApiClient
        .get(`quizzes/${id}/submissions`, { searchParams })
        .json<QuizSubmissionsResponse>();
    } catch (error: any) {
      if (error?.response?.status === 401) {
        throw new Error('You must be logged in to view submissions');
      }
      if (error?.response?.status === 403) {
        throw new Error('You do not have permission to view submissions for this quiz');
      }
      if (error?.response?.status === 404) {
        throw new Error('Quiz not found');
      }
      throw new Error(
        error?.response?.data?.message || error?.message || 'Failed to fetch submissions'
      );
    }
  },

  getUserSubmissions: async (id: number, query?: QuizSubmissionsQuery) => {
    try {
      const searchParams = query ? { ...query } : undefined;
      return await authenticatedApiClient
        .get(`quizzes/${id}/submissions/me`, { searchParams })
        .json<QuizSubmissionsResponse>();
    } catch (error: any) {
      if (error?.response?.status === 401) {
        throw new Error('You must be logged in to view your submissions');
      }
      if (error?.response?.status === 404) {
        throw new Error('Quiz not found');
      }
      throw new Error(
        error?.response?.data?.message || error?.message || 'Failed to fetch your submissions'
      );
    }
  },

  getSubmission: async (id: number) => {
    try {
      return await authenticatedApiClient.get(`submissions/${id}`).json<ApiQuizSubmission>();
    } catch (error: any) {
      if (error?.response?.status === 401) {
        throw new Error('You must be logged in to view this submission');
      }
      if (error?.response?.status === 403) {
        throw new Error('You do not have permission to view this submission');
      }
      if (error?.response?.status === 404) {
        throw new Error('Submission not found');
      }
      throw new Error(
        error?.response?.data?.message || error?.message || 'Failed to fetch submission'
      );
    }
  },

  // Analytics
  getLeaderboard: async (id: number, limit?: number) => {
    try {
      return await apiClient
        .get(`quizzes/${id}/leaderboard`, { searchParams: limit ? { limit } : undefined })
        .json<LeaderboardResponse>();
    } catch (error: any) {
      if (error?.response?.status === 403) {
        throw new Error('You do not have permission to view the leaderboard for this quiz');
      }
      if (error?.response?.status === 404) {
        throw new Error('Quiz not found');
      }
      throw new Error(
        error?.response?.data?.message || error?.message || 'Failed to fetch leaderboard'
      );
    }
  },

  getQuizAnalytics: async (id: number) => {
    try {
      return await authenticatedApiClient
        .get(`quizzes/${id}/analytics`)
        .json<QuizAnalyticsResponse>();
    } catch (error: any) {
      if (error?.response?.status === 401) {
        throw new Error('You must be logged in to view analytics');
      }
      if (error?.response?.status === 403) {
        throw new Error('You do not have permission to view analytics for this quiz');
      }
      if (error?.response?.status === 404) {
        throw new Error('Quiz not found');
      }
      throw new Error(
        error?.response?.data?.message || error?.message || 'Failed to fetch analytics'
      );
    }
  },

  getQuestionAnalytics: async (id: number) => {
    try {
      return await authenticatedApiClient
        .get(`quizzes/${id}/analytics/questions`)
        .json<QuestionAnalyticsResponse>();
    } catch (error: any) {
      if (error?.response?.status === 401) {
        throw new Error('You must be logged in to view question analytics');
      }
      if (error?.response?.status === 403) {
        throw new Error('You do not have permission to view analytics for this quiz');
      }
      if (error?.response?.status === 404) {
        throw new Error('Quiz not found');
      }
      throw new Error(
        error?.response?.data?.message || error?.message || 'Failed to fetch question analytics'
      );
    }
  },

  exportAnalytics: async (id: number) => {
    try {
      return await authenticatedApiClient.get(`quizzes/${id}/analytics/export`).text();
    } catch (error: any) {
      if (error?.response?.status === 401) {
        throw new Error('You must be logged in to export analytics');
      }
      if (error?.response?.status === 403) {
        throw new Error('You do not have permission to export analytics for this quiz');
      }
      if (error?.response?.status === 404) {
        throw new Error('Quiz not found');
      }
      throw new Error(
        error?.response?.data?.message || error?.message || 'Failed to export analytics'
      );
    }
  },

  getQuestionAnswerAnalytics: async (
    id: number,
    questionId: string,
    createdFrom?: string,
    createdTo?: string
  ) => {
    try {
      const searchParams: any = {};
      if (createdFrom) searchParams.createdFrom = createdFrom;
      if (createdTo) searchParams.createdTo = createdTo;

      return await apiClient
        .get(`quizzes/${id}/questions/${questionId}/analytics`, {
          searchParams: Object.keys(searchParams).length > 0 ? searchParams : undefined,
        })
        .json<{
          questionId: string;
          questionTitle: string;
          analytics: Array<{
            answerCombination: string[];
            count: number;
            percentage: number;
            items?: Record<string, any>;
          }>;
        }>();
    } catch (error: any) {
      if (error?.response?.status === 403) {
        throw new Error('You do not have permission to view analytics for this question');
      }
      if (error?.response?.status === 404) {
        throw new Error('Quiz or question not found');
      }
      if (error?.response?.status === 400) {
        throw new Error(
          error?.response?.data?.message || 'Analytics are only available for choice questions'
        );
      }
      throw new Error(
        error?.response?.data?.message || error?.message || 'Failed to fetch question analytics'
      );
    }
  },
};

// Analytics API
export const analyticsApi = {
  // Brand analytics
  getEntitySummary: (
    entityId: number,
    params?: { startDate?: string; endDate?: string; ignoreCache?: string }
  ) => {
    const searchParams = params ? { ...params } : undefined;
    return apiClient
      .get(`analytics/entities/${entityId}/summary`, { searchParams })
      .json<SummaryResponse>();
  },

  getBrandEngagement: (
    entityId: number,
    params?: { startDate?: string; endDate?: string; ignoreCache?: string }
  ) => {
    const searchParams = params ? { ...params } : undefined;
    return apiClient
      .get(`analytics/entities/${entityId}/engagement`, { searchParams })
      .json<EngagementAnalytics>();
  },

  getBrandTrends: (
    entityId: number,
    params?: { startDate?: string; endDate?: string; period?: string; ignoreCache?: string }
  ) => {
    const searchParams = params ? { ...params } : undefined;
    return apiClient
      .get(`analytics/entities/${entityId}/trends`, { searchParams })
      .json<TrendAnalytics>();
  },

  getBrandProducts: (
    entityId: number,
    params?: {
      sortBy?: string;
      limit?: number;
      startDate?: string;
      endDate?: string;
      ignoreCache?: string;
    }
  ) => {
    const searchParams: any = {};
    if (params?.sortBy) searchParams.sortBy = params.sortBy;
    if (params?.limit) searchParams.limit = params.limit.toString();
    if (params?.startDate) searchParams.startDate = params.startDate;
    if (params?.endDate) searchParams.endDate = params.endDate;
    if (params?.ignoreCache) searchParams.ignoreCache = params.ignoreCache;

    return apiClient
      .get(`analytics/entities/${entityId}/products`, {
        searchParams: Object.keys(searchParams).length > 0 ? searchParams : undefined,
      })
      .json<ProductInsightsResponse>();
  },

  getBrandInitiatives: (
    entityId: number,
    params?: { status?: string; startDate?: string; endDate?: string; ignoreCache?: string }
  ) => {
    const searchParams = params ? { ...params } : undefined;
    return apiClient
      .get(`analytics/entities/${entityId}/initiatives`, { searchParams })
      .json<InitiativeMetricsResponse>();
  },

  // Generic object analytics (for brands, collections, etc.)
  getObjectEngagement: (
    objectType: string,
    objectId: number,
    params?: { startDate?: string; endDate?: string }
  ) => {
    const searchParams = params ? { ...params } : undefined;
    return apiClient
      .get(`analytics/${objectType}/${objectId}/engagement`, { searchParams })
      .json<EngagementAnalytics>();
  },

  getObjectTrends: (
    objectType: string,
    objectId: number,
    params?: { startDate?: string; endDate?: string; period?: string }
  ) => {
    const searchParams = params ? { ...params } : undefined;
    return apiClient
      .get(`analytics/${objectType}s/${objectId}/analytics/trends`, { searchParams })
      .json<TrendAnalytics>();
  },

  getObjectProducts: (
    objectType: string,
    objectId: number,
    params?: { sortBy?: string; limit?: number; startDate?: string; endDate?: string }
  ) => {
    const searchParams: any = {};
    if (params?.sortBy) searchParams.sortBy = params.sortBy;
    if (params?.limit) searchParams.limit = params.limit.toString();
    if (params?.startDate) searchParams.startDate = params.startDate;
    if (params?.endDate) searchParams.endDate = params.endDate;

    return apiClient
      .get(`analytics/${objectType}s/${objectId}/analytics/products`, {
        searchParams: Object.keys(searchParams).length > 0 ? searchParams : undefined,
      })
      .json<ProductInsightsResponse>();
  },

  getObjectInitiatives: (
    objectType: string,
    objectId: number,
    params?: { status?: string; startDate?: string; endDate?: string }
  ) => {
    const searchParams = params ? { ...params } : undefined;
    return apiClient
      .get(`analytics/${objectType}s/${objectId}/analytics/initiatives`, { searchParams })
      .json<InitiativeMetricsResponse>();
  },
};

// Connections API
export const connectionsApi = {
  // Get connections with filtering
  getConnections: (query?: {
    fromType?: string;
    fromId?: number;
    toType?: string;
    toId?: number;
    toIds?: number[];
    tags?: string[];
    systemTags?: string[];
    rolesOnly?: boolean;
    roles?: string[];
    relationshipsOnly?: boolean;
    relationships?: string[];
    limit?: number;
    nextCursor?: string;
    prevCursor?: string;
    sessionHash?: string;
  }) => {
    let searchParams: any = undefined;

    if (query && Object.keys(query).length > 0) {
      searchParams = { ...query };

      // Convert tags array to comma-separated string
      if (Array.isArray(searchParams.tags)) {
        searchParams.tags = searchParams.tags.join(',');
      }

      // Convert systemTags array to comma-separated string
      if (Array.isArray(searchParams.systemTags)) {
        searchParams.systemTags = searchParams.systemTags.join(',');
      }

      // Convert roles array to comma-separated string
      if (Array.isArray(searchParams.roles)) {
        searchParams.roles = searchParams.roles.join(',');
      }

      // Convert relationships array to comma-separated string
      if (Array.isArray(searchParams.relationships)) {
        searchParams.relationships = searchParams.relationships.join(',');
      }

      // Convert toIds array to comma-separated string
      if (Array.isArray(searchParams.toIds)) {
        searchParams.toIds = searchParams.toIds.join(',');
      }

      // Remove empty values
      Object.keys(searchParams).forEach((key) => {
        if (
          searchParams[key] === '' ||
          searchParams[key] === undefined ||
          searchParams[key] === null
        ) {
          delete searchParams[key];
        }
      });

      // If all params were removed, set to undefined
      if (Object.keys(searchParams).length === 0) {
        searchParams = undefined;
      }
    }

    return authenticatedApiClient.get('connections', { searchParams }).json<{
      connections: Array<{
        id: number;
        fromId: number;
        fromType: string;
        toId: number;
        toType: string;
        tags: string[];
        systemTags: string[];
        data: Record<string, any>;
        createdAt: string;
        updatedAt: string;
        entity?: {
          id: number;
          type: string;
          name_1?: string;
          name_2?: string;
          image?: string;
          status: string;
        };
      }>;
      pagination: {
        limit: number;
        nextCursor: string | null;
        prevCursor: string | null;
        hasMore: boolean;
      };
    }>();
  },

  // Follow an entity, item, or initiative
  follow: (objectId: number, objectType: 'entity' | 'item' | 'initiative' | 'content') =>
    authenticatedApiClient
      .post('connections/follow', { json: { objectId, objectType } })
      .json<{ success: boolean; connection: any }>(),

  // Unfollow an entity, item, or initiative
  unfollow: (objectId: number, objectType: 'entity' | 'item' | 'initiative' | 'content') =>
    authenticatedApiClient
      .delete('connections/follow', { json: { objectId, objectType } })
      .json<{ success: boolean }>(),

  // Update a connection (e.g. relationships)
  updateConnection: (connectionId: number, data: { relationships?: string[] }) =>
    authenticatedApiClient
      .patch(`connections/${connectionId}`, { json: data })
      .json<{ success: boolean; connection: any }>(),

  // Track/untrack a component on a connection
  trackComponent: (data: {
    objectId: number;
    objectType: string;
    componentId: string;
    kind: string;
    name: string;
    tracked: boolean;
  }) =>
    authenticatedApiClient.post('connections/track-component', { json: data }).json<{
      success: boolean;
      connection: any;
      trackedComponents: Array<{
        componentId: string;
        kind: string;
        name: string;
        trackedAt: string;
      }>;
    }>(),
};

// Search API
export const searchApi = {
  // Search across objects with filtering
  search: (params: SearchQuery) => {
    const searchParams: any = {};

    // Add query parameter
    if (params.query) {
      searchParams.query = params.query;
    }

    // Handle objectType (can be string or array)
    if (params.objectType) {
      if (Array.isArray(params.objectType)) {
        searchParams.objectType = params.objectType.join(',');
      } else {
        searchParams.objectType = params.objectType;
      }
    }

    // Add context parameter
    if (params.context) {
      searchParams.context = params.context;
    }

    // Add owner filters
    if (params.ownerType) {
      searchParams.ownerType = params.ownerType;
    }
    if (params.ownerId !== undefined) {
      searchParams.ownerId = String(params.ownerId);
    }

    // Handle tags array
    if (params.tags && params.tags.length > 0) {
      searchParams.tags = params.tags.join(',');
    }

    // Add tagsMatch parameter (defaults to 'any' on backend if not specified)
    if (params.tagsMatch) {
      searchParams.tagsMatch = params.tagsMatch;
    }

    // Add boolean filters
    if (params.active !== undefined) {
      searchParams.active = String(params.active);
    }

    // Add date filters
    if (params.createdAfter) {
      searchParams.createdAfter = params.createdAfter;
    }
    if (params.createdBefore) {
      searchParams.createdBefore = params.createdBefore;
    }
    if (params.updatedAfter) {
      searchParams.updatedAfter = params.updatedAfter;
    }
    if (params.updatedBefore) {
      searchParams.updatedBefore = params.updatedBefore;
    }

    // Add system data filter
    if (params.systemData) {
      searchParams.systemData = JSON.stringify(params.systemData);
    }

    // Add sort order
    if (params.sortBy) {
      searchParams.sortBy = params.sortBy;
    }

    // Add pagination parameters
    if (params.limit) {
      searchParams.limit = String(params.limit);
    }
    if (params.sessionHash) {
      searchParams.sessionHash = params.sessionHash;
    }
    if (params.nextCursor) {
      searchParams.nextCursor = params.nextCursor;
    }
    if (params.prevCursor) {
      searchParams.prevCursor = params.prevCursor;
    }

    // Add engagement inclusion flag
    if (params.includeEngagement !== undefined) {
      searchParams.includeEngagement = String(params.includeEngagement);
    }

    // Add following filter
    if (params.following !== undefined) {
      searchParams.following = String(params.following);
    }

    // Add connection parameters
    if (params.connectionFromType) {
      searchParams.connectionFromType = params.connectionFromType;
    }
    if (params.connectionFromId !== undefined) {
      searchParams.connectionFromId = String(params.connectionFromId);
    }

    return apiClient
      .get('search', {
        searchParams: Object.keys(searchParams).length > 0 ? searchParams : undefined,
      })
      .json<SearchResponse>();
  },
};

// Points API
export const pointsApi = {
  // Add points to a user or entity balance
  addPoints: async (data: AddPointsParams) => {
    try {
      return await authenticatedApiClient
        .post('points/add', { json: data })
        .json<AddPointsResponse>();
    } catch (error: any) {
      if (error?.response?.status === 401) {
        throw new Error('You must be logged in to add points');
      }
      if (error?.response?.status === 403) {
        throw new Error('You do not have permission to add points');
      }
      if (error?.response?.status === 400) {
        const errorData = await error.response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || 'Invalid points data');
      }
      const errorData = await error.response?.json().catch(() => ({}));
      throw new Error(errorData.error?.message || error?.message || 'Failed to add points');
    }
  },

  // Remove points from a user or entity balance
  removePoints: async (data: RemovePointsParams) => {
    try {
      return await authenticatedApiClient
        .post('points/remove', { json: data })
        .json<RemovePointsResponse>();
    } catch (error: any) {
      if (error?.response?.status === 401) {
        throw new Error('You must be logged in to remove points');
      }
      if (error?.response?.status === 403) {
        throw new Error('You do not have permission to remove points');
      }
      if (error?.response?.status === 400) {
        const errorData = await error.response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || 'Invalid points data');
      }
      const errorData = await error.response?.json().catch(() => ({}));
      throw new Error(errorData.error?.message || error?.message || 'Failed to remove points');
    }
  },

  // Transfer points between users or entities
  transferPoints: async (data: TransferPointsParams) => {
    try {
      return await authenticatedApiClient
        .post('points/transfer', { json: data })
        .json<TransferPointsResponse>();
    } catch (error: any) {
      if (error?.response?.status === 401) {
        throw new Error('You must be logged in to transfer points');
      }
      if (error?.response?.status === 403) {
        throw new Error('You do not have permission to transfer points');
      }
      if (error?.response?.status === 400) {
        const errorData = await error.response.json().catch(() => ({}));
        if (errorData.error?.code === 'INSUFFICIENT_BALANCE') {
          throw new Error('Insufficient points balance');
        }
        if (errorData.error?.code === 'SELF_TRANSFER') {
          throw new Error('Cannot transfer points to yourself');
        }
        throw new Error(errorData.error?.message || 'Invalid transfer data');
      }
      const errorData = await error.response?.json().catch(() => ({}));
      throw new Error(errorData.error?.message || error?.message || 'Failed to transfer points');
    }
  },

  // Spend points on platform features
  spendPoints: async (data: SpendPointsParams) => {
    try {
      return await authenticatedApiClient
        .post('points/spend', { json: data })
        .json<SpendPointsResponse>();
    } catch (error: any) {
      if (error?.response?.status === 401) {
        throw new Error('You must be logged in to spend points');
      }
      if (error?.response?.status === 403) {
        throw new Error('You do not have permission to spend points');
      }
      if (error?.response?.status === 400) {
        const errorData = await error.response.json().catch(() => ({}));
        if (errorData.error?.code === 'INSUFFICIENT_BALANCE') {
          throw new Error('Insufficient points balance');
        }
        throw new Error(errorData.error?.message || 'Invalid spend data');
      }
      const errorData = await error.response?.json().catch(() => ({}));
      throw new Error(errorData.error?.message || error?.message || 'Failed to spend points');
    }
  },

  // Get current points balance
  getBalance: async (objectType: 'user' | 'entity' = 'user', objectId?: number) => {
    try {
      const searchParams: any = {};
      if (objectType) searchParams.objectType = objectType;
      if (objectId !== undefined) searchParams.objectId = objectId.toString();

      return await authenticatedApiClient
        .get('points/balance', {
          searchParams: Object.keys(searchParams).length > 0 ? searchParams : undefined,
        })
        .json<PointsBalance>();
    } catch (error: any) {
      if (error?.response?.status === 401) {
        throw new Error('You must be logged in to view points balance');
      }
      if (error?.response?.status === 403) {
        throw new Error('You do not have permission to view this balance');
      }
      if (error?.response?.status === 404) {
        throw new Error('User or entity not found');
      }
      const errorData = await error.response?.json().catch(() => ({}));
      throw new Error(errorData.error?.message || error?.message || 'Failed to get balance');
    }
  },

  // Get transaction history
  getHistory: async (query?: PointsHistoryQuery) => {
    try {
      const searchParams: any = {};
      if (query?.objectType) searchParams.objectType = query.objectType;
      if (query?.objectId !== undefined) searchParams.objectId = query.objectId.toString();
      if (query?.limit) searchParams.limit = query.limit.toString();
      if (query?.nextCursor) searchParams.nextCursor = query.nextCursor;
      if (query?.prevCursor) searchParams.prevCursor = query.prevCursor;

      return await authenticatedApiClient
        .get('points/history', {
          searchParams: Object.keys(searchParams).length > 0 ? searchParams : undefined,
        })
        .json<PointsHistoryResponse>();
    } catch (error: any) {
      if (error?.response?.status === 401) {
        throw new Error('You must be logged in to view transaction history');
      }
      if (error?.response?.status === 403) {
        throw new Error('You do not have permission to view this history');
      }
      const errorData = await error.response?.json().catch(() => ({}));
      throw new Error(errorData.error?.message || error?.message || 'Failed to get history');
    }
  },

  // Get aggregated points stats grouped by target
  getStats: async (
    objectType: 'user' | 'entity' = 'user',
    objectId?: number,
    targetIds?: string[]
  ) => {
    try {
      const searchParams: any = {};
      if (objectType) searchParams.objectType = objectType;
      if (objectId !== undefined) searchParams.objectId = objectId.toString();
      if (targetIds && targetIds.length > 0) searchParams.targetIds = targetIds.join(',');

      return await authenticatedApiClient
        .get('points/stats', {
          searchParams: Object.keys(searchParams).length > 0 ? searchParams : undefined,
        })
        .json<PointsStatsResponse>();
    } catch (error: any) {
      if (error?.response?.status === 401) {
        throw new Error('You must be logged in to view points stats');
      }
      const errorData = await error.response?.json().catch(() => ({}));
      throw new Error(errorData.error?.message || error?.message || 'Failed to get points stats');
    }
  },
};

// Messages API
export const messagesApi = {
  // Send enquiry
  sendEnquiry: (
    data: {
      resourceType: string;
      resourceId: number;
      enquiryType: string;
      subject?: string;
      message: string;
      data?: Record<string, unknown>;
    },
    opts?: {
      route?: string;
    }
  ) =>
    authenticatedApiClient.post(opts?.route || 'messaging/enquiries', { json: data }).json<any>(),

  // Get threads for authenticated user
  getThreads: (query?: {
    resource_type?: string;
    resource_id?: number;
    thread_type?: string;
    archived?: boolean;
    limit?: number;
    next_cursor?: string;
    prev_cursor?: string;
  }) => {
    const searchParams: any = {};
    if (query?.resource_type) searchParams.resource_type = query.resource_type;
    if (query?.resource_id !== undefined) searchParams.resource_id = query.resource_id.toString();
    if (query?.thread_type) searchParams.thread_type = query.thread_type;
    if (query?.archived !== undefined) searchParams.archived = query.archived.toString();
    if (query?.limit) searchParams.limit = query.limit.toString();
    if (query?.next_cursor) searchParams.next_cursor = query.next_cursor;
    if (query?.prev_cursor) searchParams.prev_cursor = query.prev_cursor;

    return authenticatedApiClient
      .get('messaging/threads', {
        searchParams: Object.keys(searchParams).length > 0 ? searchParams : undefined,
      })
      .json<any>();
  },

  // Create new thread with initial message
  createThread: (data: {
    name?: string;
    participants: Array<{ type: string; id: number; role?: string }>;
    resourceType?: string;
    resourceId?: number;
    threadType?: string;
    initialMessage: {
      messageType?: string;
      subject?: string;
      message: string;
      data?: Record<string, unknown>;
    };
  }) => authenticatedApiClient.post('messaging/threads', { json: data }).json<any>(),

  // Find thread by participant IDs
  findThreadByParticipants: (participantIds: number[]) =>
    authenticatedApiClient
      .post('messaging/threads/find-by-participants', { json: { participantIds } })
      .json<{ thread: any | null }>(),

  // Update thread metadata
  updateThread: (
    threadId: string,
    data: {
      name?: string;
      participants?: Array<{ type: string; id: number; role?: string }>;
      archived?: boolean;
    }
  ) => authenticatedApiClient.patch(`messaging/threads/${threadId}`, { json: data }).json<any>(),

  // Get messages in a thread
  getMessages: (
    threadId: string,
    query?: {
      limit?: number;
      next_cursor?: string;
      prev_cursor?: string;
    }
  ) => {
    const searchParams: any = {};
    if (query?.limit) searchParams.limit = query.limit.toString();
    if (query?.next_cursor) searchParams.next_cursor = query.next_cursor;
    if (query?.prev_cursor) searchParams.prev_cursor = query.prev_cursor;

    return authenticatedApiClient
      .get(`messaging/threads/${threadId}/messages`, {
        searchParams: Object.keys(searchParams).length > 0 ? searchParams : undefined,
      })
      .json<any>();
  },

  // Send message to thread
  sendMessage: (
    threadId: string,
    data: {
      message: string;
      messageType?: string;
      data?: Record<string, unknown>;
    }
  ) =>
    authenticatedApiClient
      .post(`messaging/threads/${threadId}/messages`, { json: data })
      .json<any>(),

  // Mark messages as read
  markAsRead: (threadId: string, messageIds: string[]) =>
    authenticatedApiClient
      .post(`messaging/threads/${threadId}/read`, { json: { messageIds } })
      .json<any>(),

  // Search messages
  searchMessages: (query?: {
    q?: string;
    status?: string;
    messageType?: string;
    resourceType?: string;
    sort?: string;
    limit?: number;
    next_cursor?: string;
    prev_cursor?: string;
  }) => {
    const searchParams: any = {};
    if (query?.q) searchParams.q = query.q;
    if (query?.status) searchParams.status = query.status;
    if (query?.messageType) searchParams.messageType = query.messageType;
    if (query?.resourceType) searchParams.resourceType = query.resourceType;
    if (query?.sort) searchParams.sort = query.sort;
    if (query?.limit) searchParams.limit = query.limit.toString();
    if (query?.next_cursor) searchParams.next_cursor = query.next_cursor;
    if (query?.prev_cursor) searchParams.prev_cursor = query.prev_cursor;

    return authenticatedApiClient
      .get('messaging/search', {
        searchParams: Object.keys(searchParams).length > 0 ? searchParams : undefined,
      })
      .json<any>();
  },
};

// Trade API
export const tradeApi = {
  // Get hub settings
  getSettings: (resourceType: string, resourceId: number) => {
    const searchParams = {
      resource_type: resourceType,
      resource_id: resourceId.toString(),
    };
    return authenticatedApiClient.get('trade/settings', { searchParams }).json<any>();
  },

  // Update hub settings
  updateSettings: (data: {
    resourceType: string;
    resourceId: number;
    visibility?: string;
    allowedUserIds?: number[];
    details?: string;
    contactDetails?: {
      email?: string;
      phone?: string;
      website?: string;
    };
    minOrderQuantity?: number;
    terms?: string;
    images?: string[];
    documents?: Array<{ name: string; url: string; size: number }>;
    sampleRequestsEnabled?: boolean;
    meetingRequestsEnabled?: boolean;
  }) => authenticatedApiClient.patch('trade/settings', { json: data }).json<any>(),

  // Discover hubs
  discover: (query?: { resource_type?: string; category?: string; limit?: number }) => {
    const searchParams: any = {};
    if (query?.resource_type) searchParams.resource_type = query.resource_type;
    if (query?.category) searchParams.category = query.category;
    if (query?.limit) searchParams.limit = query.limit.toString();

    return authenticatedApiClient
      .get('trade/discover', {
        searchParams: Object.keys(searchParams).length > 0 ? searchParams : undefined,
      })
      .json<any>();
  },

  // Get trade analytics
  getTradeAnalytics: (params: {
    resourceType: string;
    resourceId: number;
    startDate?: string;
    endDate?: string;
  }) => {
    const searchParams: any = {
      resource_type: params.resourceType,
      resource_id: params.resourceId.toString(),
    };
    if (params.startDate) searchParams.start_date = params.startDate;
    if (params.endDate) searchParams.end_date = params.endDate;

    return authenticatedApiClient.get('trade/analytics', { searchParams }).json<{
      enquiryCount: number;
      sampleRequestCount: number;
      sampleRequestFulfillmentRate: number;
      averageResponseTime: number;
      topSources: Array<{
        userId: number;
        userName: string;
        enquiryCount: number;
      }>;
    }>();
  },

  // Endpoint path helpers
  entityPath: (id: number) => `entities/${id}`,
  tradeHubSettingsPath: (resourceType: string, resourceId: number) =>
    `trade/settings?resource_type=${resourceType}&resource_id=${resourceId}`,
  tradeEnquiriesPath: 'trade/enquiries' as const,
  tradeDiscoverPath: 'trade/discover' as const,
  tradeAnalyticsPath: (resourceType: string, resourceId: number) =>
    `trade/analytics?resource_type=${resourceType}&resource_id=${resourceId}`,
};

// Maps API
export const mapsApi = {
  // Get place autocomplete predictions
  getPlaceAutocomplete: (params: {
    input: string;
    sessionToken: string;
    types?: string;
    components?: string;
  }) => {
    const searchParams: any = {
      input: params.input,
      sessionToken: params.sessionToken,
    };
    if (params.types) searchParams.types = params.types;
    if (params.components) searchParams.components = params.components;

    return apiClient.get('maps/autocomplete', { searchParams }).json<{
      predictions: Array<{
        id: string;
        name: string;
        description: string;
        secondary?: string;
        types: string[];
        source_provider: string;
        source_reference: string;
      }>;
    }>();
  },

  // Get place details
  getPlaceDetails: (params: { id: string; sessionToken: string; fields?: string }) => {
    const searchParams: any = {
      id: params.id,
      sessionToken: params.sessionToken,
    };
    if (params.fields) searchParams.fields = params.fields;

    return apiClient.get('maps/details', { searchParams }).json<{
      status: string;
      result: {
        place_id: string;
        name: string;
        formatted_address: string;
        display_address: string;
        geometry: {
          location: {
            lat: number;
            lng: number;
          };
        };
        address_components: Array<{
          long_name: string;
          short_name: string;
          types: string[];
        }>;
        types: string[];
        url?: string;
        business_status?: string;
        vicinity?: string;
      };
    }>();
  },
};

// Content API
export const contentApi = {
  // Get content with filtering
  getContent: (query?: ContentQuery) => {
    const searchParams = { ...query } as any;
    if (Array.isArray(searchParams.ids)) {
      searchParams.ids = searchParams.ids.join(',');
    }
    if (Array.isArray(searchParams.type)) {
      searchParams.type = searchParams.type.join(',');
    }
    if (Array.isArray(searchParams.tags)) {
      searchParams.tags = searchParams.tags.join(',');
    }
    if (Array.isArray(searchParams.systemTags)) {
      searchParams.systemTags = searchParams.systemTags.join(',');
    }
    if (searchParams.tagExpression && typeof searchParams.tagExpression === 'object') {
      searchParams.tagExpression = JSON.stringify(searchParams.tagExpression);
    }
    if (searchParams.deleted !== undefined) {
      searchParams.deleted = searchParams.deleted.toString();
    }
    return apiClient.get('content', { searchParams }).json<ContentResponse>();
  },

  // Get single content by ID or slug
  getContentItem: (id: number | string) => apiClient.get(`content/${id}`).json<ApiContent>(),

  // Create content (authenticated)
  createContent: async (data: CreateContentRequest) => {
    try {
      return await authenticatedApiClient.post('content', { json: data }).json<ApiContent>();
    } catch (error: any) {
      throw toApiError(error);
    }
  },

  // Update content (authenticated)
  updateContent: async (id: number, data: UpdateContentRequest) => {
    try {
      return await authenticatedApiClient.put(`content/${id}`, { json: data }).json<ApiContent>();
    } catch (error: any) {
      throw toApiError(error);
    }
  },
};

// Access Grants API
export const accessGrantsApi = {
  // List grants. Either pass `entityScopeId` to see all grants for an
  // entity (and its initiatives), or pass an explicit objectType + objectId
  // to scope to a single object.
  getGrants: (query: AccessGrantsQuery) => {
    const searchParams: Record<string, string> = {};
    if (query.entityScopeId !== undefined) {
      searchParams.entityScopeId = String(query.entityScopeId);
    }
    if (query.objectType) searchParams.objectType = query.objectType;
    if (query.objectId !== undefined) searchParams.objectId = String(query.objectId);
    if (query.search) searchParams.search = query.search;
    if (query.includeRevoked) searchParams.includeRevoked = 'true';
    if (query.limit !== undefined) searchParams.limit = String(query.limit);
    if (query.nextCursor) searchParams.nextCursor = query.nextCursor;
    return authenticatedApiClient
      .get('access-grants', { searchParams })
      .json<AccessGrantsResponse>();
  },

  // Get a single grant
  getGrant: (id: number) =>
    authenticatedApiClient.get(`access-grants/${id}`).json<ApiAccessGrant>(),

  // Create a grant. The raw token is included in the response exactly once.
  createGrant: async (data: CreateAccessGrantRequest) => {
    try {
      return await authenticatedApiClient
        .post('access-grants', { json: data })
        .json<CreateAccessGrantResponse>();
    } catch (error: any) {
      throw toApiError(error);
    }
  },

  // Update a grant (label, permissions, expiry, maxUses)
  updateGrant: async (id: number, data: UpdateAccessGrantRequest) => {
    try {
      return await authenticatedApiClient
        .patch(`access-grants/${id}`, { json: data })
        .json<ApiAccessGrant>();
    } catch (error: any) {
      throw toApiError(error);
    }
  },

  // Revoke a grant (soft, preserves audit trail)
  revokeGrant: (id: number) =>
    authenticatedApiClient
      .delete(`access-grants/${id}`, { json: {} })
      .json<{ message: string; grant: ApiAccessGrant }>(),
};

// Dynamic Items API
export const dynamicItemsApi = {
  // Get dynamic items with filtering
  getDynamicItems: (query?: DynamicItemsQuery) => {
    const searchParams: Record<string, string> = {};
    if (query?.limit !== undefined) searchParams.limit = String(query.limit);
    if (query?.type) {
      searchParams.type = Array.isArray(query.type) ? query.type.join(',') : query.type;
    }
    if (query?.status) searchParams.status = query.status;
    if (query?.visibility) searchParams.visibility = query.visibility;
    if (query?.search) searchParams.search = query.search;
    if (query?.parentId !== undefined) searchParams.parentId = String(query.parentId);
    if (query?.ownerType) searchParams.ownerType = query.ownerType;
    if (query?.ownerId) searchParams.ownerId = query.ownerId;
    if (query?.tags) searchParams.tags = query.tags.join(',');
    if (query?.systemTags) searchParams.systemTags = query.systemTags.join(',');
    if (query?.includeDeleted) searchParams.includeDeleted = 'true';
    if (query?.nextCursor) searchParams.nextCursor = query.nextCursor;
    if (query?.prevCursor) searchParams.prevCursor = query.prevCursor;
    return apiClient.get('dynamic-items', { searchParams }).json<DynamicItemsResponse>();
  },

  // Get single dynamic item by ID or slug
  getDynamicItem: (id: number | string) =>
    apiClient.get(`dynamic-items/${id}`).json<ApiDynamicItem>(),

  // Create dynamic item (authenticated)
  createDynamicItem: async (data: CreateDynamicItemRequest) => {
    try {
      return await authenticatedApiClient
        .post('dynamic-items', { json: data })
        .json<ApiDynamicItem>();
    } catch (error: any) {
      throw toApiError(error);
    }
  },

  // Update dynamic item (authenticated)
  updateDynamicItem: async (id: number, data: UpdateDynamicItemRequest) => {
    try {
      return await authenticatedApiClient
        .put(`dynamic-items/${id}`, { json: data })
        .json<ApiDynamicItem>();
    } catch (error: any) {
      throw toApiError(error);
    }
  },

  // Delete dynamic item (authenticated, soft delete)
  deleteDynamicItem: async (id: number) => {
    try {
      return await authenticatedApiClient
        .delete(`dynamic-items/${id}`, { json: {} })
        .json<{ message: string }>();
    } catch (error: any) {
      throw toApiError(error);
    }
  },
};
